/**
 * Day 8, strategic opportunities, from that stage onward: each candidate's
 * findings, target fit, assets, gates and adaptation before the skeptical
 * review controls, and the immutable approved opportunities.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  generateAnalysisOpportunityCandidates,
  newIdempotencyKey,
  reviewAnalysisOpportunities,
  type AnalysisDay8State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import { parseObjectJson } from './review-json.js';
import { card } from './run-page-styles.js';
import { displayScalar, toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

export function OpportunitiesSection({
  id,
  run,
  day8,
  busy,
  mutate,
  drafts,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day8: AnalysisDay8State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
}): ReactNode {
  const { opportunityReviewJson, setOpportunityReviewJson } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('STRATEGIC_OPPORTUNITIES')
  ) {
    return null;
  }

  return (
    <section id="opportunities-review" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>Strategic opportunities</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          An approved finding is not a recommendation. Each candidate below preserves
          the target-fit, target-evidence, asset, feasibility, safety and adaptation
          gates that must survive before it becomes an immutable opportunity.
        </p>
        {run.currentStage === 'STRATEGIC_OPPORTUNITIES' &&
        run.executionState === 'PENDING' ? (
          <Button
            variant="primary"
            busy={busy === 'opportunity-candidates'}
            onClick={() =>
              void mutate('opportunity-candidates', () =>
                generateAnalysisOpportunityCandidates(id, newIdempotencyKey()),
              )
            }
          >
            Evaluate target-specific opportunity candidates
          </Button>
        ) : null}
      </div>

      {day8.opportunityCandidates.length === 0 && day8.opportunityCandidateManifest ? (
        <InlineAlert
          tone="warning"
          title="Zero opportunities survived generation. This is a valid analytical result; none will be manufactured."
        />
      ) : null}

      {day8.opportunityCandidates.length > 0 ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          {day8.opportunityCandidates.map((artifact) => {
            const payload = artifact.payload;
            const gates =
              typeof payload['gates'] === 'object' &&
              payload['gates'] !== null &&
              !Array.isArray(payload['gates'])
                ? (payload['gates'] as Record<string, unknown>)
                : {};
            const validity =
              typeof payload['validity'] === 'string' ? payload['validity'] : 'UNKNOWN';
            return (
              <article key={artifact.id} style={card}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone={toneFor(validity)}>{validity}</Tag>
                  <Tag tone="info">
                    {typeof payload['opportunityFamily'] === 'string'
                      ? payload['opportunityFamily']
                      : 'UNKNOWN_FAMILY'}
                  </Tag>
                  <Tag tone="info">
                    Offer{' '}
                    {typeof payload['selectedOffer'] === 'object' &&
                    payload['selectedOffer'] !== null
                      ? displayScalar(
                          (payload['selectedOffer'] as Record<string, unknown>)[
                            'title'
                          ],
                          'unknown',
                        )
                      : 'unknown'}
                  </Tag>
                </div>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {typeof payload['statement'] === 'string'
                    ? payload['statement']
                    : 'Opportunity wording unavailable'}
                </h3>
                <h4>Supporting findings</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(
                    payload['supportingFindingArtifactIds'] ?? [],
                    null,
                    2,
                  )}
                </pre>
                <h4>Target fit and target evidence</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(
                    {
                      targetFit: payload['targetRelevanceBasis'] ?? [],
                      targetEvidence: payload['targetEvidence'] ?? {},
                    },
                    null,
                    2,
                  )}
                </pre>
                <h4>Assets, feasibility and constraints</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(
                    {
                      assets: payload['proofAssetsAvailable'] ?? [],
                      feasibility: payload['feasibility'] ?? {},
                      constraints: payload['constraints'] ?? [],
                      counterEvidence: payload['counterEvidence'] ?? [],
                    },
                    null,
                    2,
                  )}
                </pre>
                <h4>Eligibility gates</h4>
                <div style={{ display: 'grid', gap: 8 }}>
                  {Object.entries(gates).map(([name, value]) => {
                    const gate =
                      typeof value === 'object' &&
                      value !== null &&
                      !Array.isArray(value)
                        ? (value as Record<string, unknown>)
                        : {};
                    const gateState = displayScalar(gate['state'], 'UNKNOWN');
                    return (
                      <div key={name} style={{ ...card, padding: 10 }}>
                        <Tag tone={toneFor(gateState)}>{gateState}</Tag>{' '}
                        <strong>{name}</strong>
                        <p style={{ marginBottom: 0 }}>
                          {displayScalar(gate['basis'], 'No basis recorded.')}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <h4>Copying / adaptation</h4>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(payload['copyingRisk'] ?? {}, null, 2)}
                </pre>
                <p style={{ color: 'var(--text-muted)', marginBottom: 0 }}>
                  Candidate artifact {artifact.id} · immutable hash{' '}
                  {artifact.contentHash}
                </p>
              </article>
            );
          })}
        </div>
      ) : null}

      {run.currentStage === 'STRATEGIC_OPPORTUNITIES' &&
      run.executionState === 'AWAITING_REVIEW' ? (
        <div style={{ ...card, display: 'grid', gap: 12, marginTop: 12 }}>
          <InlineAlert
            tone="warning"
            title="Nothing is auto-approved. Use APPROVED, REJECTED, CORRECTED, CONDITIONAL, or INSUFFICIENT_EVIDENCE and give every candidate a reason."
          />
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            CORRECTED may change final wording only. It cannot change the Offer,
            supporting findings, evidence, gates, assets, constraints, or feasibility.
            Add a unique order from 1–3 to any approved, corrected, or conditional item.
          </p>
          <TextAreaField
            label="Strategic-opportunity decisions JSON"
            value={opportunityReviewJson}
            onChange={setOpportunityReviewJson}
            rows={16}
            maxLength={1_800_000}
          />
          <Button
            variant="primary"
            busy={busy === 'opportunity-review'}
            onClick={() =>
              void mutate('opportunity-review', () =>
                reviewAnalysisOpportunities(
                  id,
                  newIdempotencyKey(),
                  opportunityReviewJson.trim()
                    ? parseObjectJson(
                        opportunityReviewJson,
                        'Strategic-opportunity review',
                      )
                    : { decisions: [] },
                ),
              )
            }
          >
            Freeze reviewed strategic opportunities
          </Button>
        </div>
      ) : null}

      {day8.approvedOpportunities.length > 0 ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>
            Immutable approved opportunities ({day8.approvedOpportunities.length})
          </summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {day8.approvedOpportunities.map((opportunity) => (
              <article key={opportunity.id} style={card}>
                <Tag tone="success">APPROVED OPPORTUNITY</Tag>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {typeof opportunity.payload['finalWording'] === 'string'
                    ? opportunity.payload['finalWording']
                    : 'Approved opportunity'}
                </h3>
                <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {JSON.stringify(opportunity.payload, null, 2)}
                </pre>
              </article>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  );
}
